import { Component, ElementRef, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { trigger, transition, style, animate } from '@angular/animations';
import {
  AuthService,
  EmployeeAccountConfirmationRequest,
  INVITATION_PREVIEW_INVALID,
  InvitationPreview,
} from 'src/app/modules/auth/services/auth/auth.service';
import { NewPassword } from '../../../admin/model/new-password.model';
import { GDPR_CONSTANTS } from '../../../../core/constants/gdpr.constants';
import { ThemeService } from 'src/app/core/services/theme/theme.service';
import { EMPLOYEE_CONFIRMATION_TOO_MANY_ATTEMPTS, employeeConfirmationErrorText } from '../../models/auth-error-text';
import { XdsButtonComponent, XdsIconButtonComponent, XdsBannerComponent, XdsCheckboxComponent, XdsInputComponent, XdsFormFieldComponent } from '../../../../shared/ui';

@Component({
    selector: 'app-accept-invitation',
    templateUrl: './accept-invitation.component.html',
    styleUrls: ['./accept-invitation.component.css'],
    imports: [CommonModule, ReactiveFormsModule, TranslateModule, XdsButtonComponent, XdsIconButtonComponent, XdsBannerComponent, XdsCheckboxComponent, XdsInputComponent, XdsFormFieldComponent],
    animations: [
      trigger('fadeIn', [
        transition(':enter', [
          style({ opacity: 0, transform: 'translateY(20px)' }),
          animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
        ])
      ])
    ]
})
export class AcceptInvitationComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  formModel: NewPassword = {
    Email: null,
    OldPassword: null,
    NewPassword: null,
  };

  EmployeeName = '';
  newPasswordForm: UntypedFormGroup;
  newPassword: string;
  token: string;
  email: string;
  oldPass: string = '';
  iid: string;

  showPassword = false;
  showConfirmPassword = false;
  passwordStrength = 0;

  // ========================================
  // GDPR Constants & State (Added: 2025-10-19)
  // ========================================
  public readonly GDPR_CONSTANTS = GDPR_CONSTANTS;
  showConsentDetails = false;
  private userAgent: string;

  // ========================================
  // Wizard State (Added: 2025-10-19)
  // ========================================
  currentStep: 1 | 2 = 1;
  isTransitioning = false;

  /**
   * Ablehnung des letzten Absendens als Text im Formular (BE-12 P1, R7) — statt des Toasts, den
   * vorher der HttpService zeigte. `null` = keine Meldung.
   */
  submitError: string | null = null;
  /** „Zur Anmeldung“ nur beim Sammeltext `TOKEN_INVALID_OR_EXPIRED` (auch: Link schon benutzt). */
  showLoginAction = false;

  /**
   * Vorabruf der Einladungsdaten (BE-12 P4): Erst wenn er gelingt, erscheint der Assistent. Die
   * E-Mail-Adresse zeigt die Seite nur als Maske vom Server, nie aus der URL.
   */
  previewState: 'loading' | 'ready' | 'error' = 'loading';
  preview: InvitationPreview | null = null;
  /** Übersetzter Text, wenn der Vorabruf scheitert (Link ungültig/abgelaufen/benutzt, 429). */
  previewError: string | null = null;
  /** „Zur Anmeldung“ beim ungültigen Link (auch: schon benutzt) — nicht bei „zu viele Versuche“. */
  previewShowLogin = false;

  @ViewChild('previewErrorBanner', { read: ElementRef })
  set previewErrorBanner(ref: ElementRef<HTMLElement> | undefined) {
    ref?.nativeElement.focus();
  }

  /** Fokus auf die Fehlermeldung, sobald sie erscheint (WCAG 2.4.3/4.1.3, Muster Welle A). */
  @ViewChild('submitErrorBanner', { read: ElementRef })
  set submitErrorBanner(ref: ElementRef<HTMLElement> | undefined) {
    ref?.nativeElement.focus();
  }

  // inject() statt Konstruktor-Parameter: im Vitest (esbuild, ohne Decorator-Metadaten) sonst nicht baubar.
  private route = inject(ActivatedRoute);
  private authService = inject(AuthService);
  private fb = inject(UntypedFormBuilder);
  private translate = inject(TranslateService);
  public themeService = inject(ThemeService);

  constructor() {
    // WICHTIG: Arrays behandeln - nur das erste Element nehmen
    const tokenParam = this.route.snapshot.queryParams.token;
    const emailParam = this.route.snapshot.queryParams.email;
    const passParam = this.route.snapshot.queryParams.pass;
    const iidParam = this.route.snapshot.queryParams.iid;

    // Wenn es Arrays sind, nimm das erste Element
    this.token = Array.isArray(tokenParam) ? tokenParam[0] : tokenParam;
    // Nur für den Absende-Request (der Server prüft sie gegen die Einladung); angezeigt wird
    // ausschließlich die Maske aus `InvitationPreview` (BE-12 P4).
    this.email = Array.isArray(emailParam) ? emailParam[0] : emailParam;
    this.oldPass = Array.isArray(passParam) ? passParam[0] : passParam || '';
    this.iid = Array.isArray(iidParam) ? iidParam[0] : iidParam || '';
  }

  /** Begrüßung mit Vorname und Firma (Vorname kann fehlen). */
  get greetingText(): string {
    const p = this.preview;
    if (!p) return '';
    const params = { firstName: p.firstName, companyName: p.companyName };
    return this.translate.instant(p.firstName ? 'ACCEPT_INV_GREETING' : 'ACCEPT_INV_GREETING_NO_NAME', params);
  }

  private loadPreview(): void {
    this.previewState = 'loading';
    this.previewError = null;
    this.previewShowLogin = false;
    if (!this.iid || !this.token) {
      // Unvollständiger Link: kein Serveraufruf, gleiche Meldung wie ein ungültiger Link.
      this.showPreviewError(INVITATION_PREVIEW_INVALID);
      return;
    }
    this.authService
      .InvitationPreview(this.iid, this.token)
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        // `in` statt `result.ok`: ohne strictNullChecks engt TypeScript die Union über `ok` nicht ein.
        if ('preview' in result) {
          this.preview = result.preview;
          this.previewState = 'ready';
        } else {
          this.showPreviewError(result.code);
        }
      });
  }

  private showPreviewError(code: string): void {
    const tooMany = code === EMPLOYEE_CONFIRMATION_TOO_MANY_ATTEMPTS;
    this.preview = null;
    this.previewState = 'error';
    this.previewError = this.translate.instant(tooMany ? EMPLOYEE_CONFIRMATION_TOO_MANY_ATTEMPTS : 'ACCEPT_INV_INVALID');
    this.previewShowLogin = !tooMany;
  }

  ngOnInit(): void {
    this.newPasswordForm = this.fb.group(
      {
        password: ['', [Validators.required, Validators.minLength(12), this.validatePasswordStrength.bind(this)]],
        confirmPassword: [''],

        // ========================================
        // DSGVO Consent Felder (Added: 2025-10-19)
        // ========================================
        // Eingeladene Mitarbeiter akzeptieren KEINE B2B-AGB (Nutzungsbedingungen) –
        // Vertragspartei ist der Arbeitgeber. Nur Datenschutz-Kenntnisnahme ist Pflicht.
        privacyPolicyAccepted: [false, Validators.requiredTrue], // Kenntnisnahme der Datenschutzinformationen (Art. 13 DSGVO)
        marketingConsent: [false], // Optional
      },
      { validator: this.checkPasswords }
    );

    // Subscribe to password changes for strength indicator
    this.newPasswordForm.get('password')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value) => {
      this.passwordStrength = this.calculatePasswordStrength(value);
    });

    // Capture user agent for audit trail
    this.userAgent = navigator.userAgent;

    this.loadPreview();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  checkPasswords(group: UntypedFormGroup): any {
    const pass = group.get('password')?.value;
    const confirmPass = group.get('confirmPassword')?.value;
    return pass === confirmPass ? null : { notSame: true };
  }

  calculatePasswordStrength(password: string): number {
    if (!password) return 0;

    let strength = 0;

    // Länge >= 12
    if (password.length >= 12) strength++;

    // Enthält Großbuchstaben
    if (/[A-Z]/.test(password)) strength++;

    // Enthält Kleinbuchstaben
    if (/[a-z]/.test(password)) strength++;

    // Enthält Ziffern
    if (/[0-9]/.test(password)) strength++;

    // Enthält Sonderzeichen
    if (/[^A-Za-z0-9]/.test(password)) strength++;

    return strength;
  }

  getPasswordStrengthLabel(): string {
    switch (this.passwordStrength) {
      case 0:
      case 1:
        return 'Schwach';
      case 2:
        return 'Mittel';
      case 3:
        return 'Gut';
      case 4:
        return 'Stark';
      case 5:
        return 'Sehr stark';
      default:
        return '';
    }
  }

  validatePasswordStrength(control: any): { [key: string]: boolean } | null {
    const value = control.value || '';
    const errors: { [key: string]: boolean } = {};

    if (value.length < 12) {
      errors['minlength'] = true;
    }
    if (!/[A-Z]/.test(value)) {
      errors['missingUpper'] = true;
    }
    if (!/[a-z]/.test(value)) {
      errors['missingLower'] = true;
    }
    if (!/[0-9]/.test(value)) {
      errors['missingDigit'] = true;
    }
    if (!/[^A-Za-z0-9]/.test(value)) {
      errors['missingSpecial'] = true;
    }

    return Object.keys(errors).length ? errors : null;
  }

  /**
   * Erste zutreffende Passwort-Fehlermeldung (i18n-Key) für die zentrale
   * xds-form-field-Fehlerzeile. Ersetzt die früher handgebaute Liste aus
   * mehreren <span class="error-message">-Zeilen (Priorität wie zuvor).
   */
  get passwordErrorKey(): string {
    const errors = this.newPasswordForm?.get('password')?.errors;
    if (!errors) return '';
    if (errors['required']) return 'This field is required';
    if (errors['minlength']) return 'PASSWORD_MIN_LENGTH';
    if (errors['missingUpper']) return 'PASSWORD_MISSING_UPPER';
    if (errors['missingLower']) return 'PASSWORD_MISSING_LOWER';
    if (errors['missingDigit']) return 'PASSWORD_MISSING_DIGIT';
    if (errors['missingSpecial']) return 'PASSWORD_MISSING_SPECIAL';
    return '';
  }

  toggleShowPassword(): void {
    this.showPassword = !this.showPassword;
  }

  toggleShowConfirmPassword(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  /**
   * Password blur handler - DOES NOT SUBMIT, only logs
   */
  onPasswordBlur(): void {
  }

  /**
   * Confirm password blur handler - DOES NOT SUBMIT, only logs
   */
  onPasswordConfirmBlur(): void {
  }

  // ========================================
  // GDPR Form Control Getters (Added: 2025-10-19)
  // ========================================
  get privacyPolicyAccepted() { return this.newPasswordForm.get('privacyPolicyAccepted'); }
  get marketingConsent() { return this.newPasswordForm.get('marketingConsent'); }

  /**
   * Toggle display of detailed consent information
   */
  toggleConsentDetails(): void {
    this.showConsentDetails = !this.showConsentDetails;
  }

  /**
   * Open external link in new tab
   */
  openLink(url: string, event: Event): void {
    event.preventDefault();
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  // ========================================
  // Wizard Navigation (Added: 2025-10-19)
  // ========================================

  /**
   * Check if all required GDPR consents are given
   */
  get canProceedToStep2(): boolean {
    return !!this.newPasswordForm.get('privacyPolicyAccepted')?.value;
  }

  /**
   * Get consent progress count
   * Nur eine Pflicht-Einwilligung (Datenschutz-Kenntnisnahme).
   */
  getConsentCount(): { given: number; total: number } {
    const privacyAccepted = this.newPasswordForm.get('privacyPolicyAccepted')?.value;

    const given = [privacyAccepted].filter(Boolean).length;
    return { given, total: 1 };
  }

  /**
   * Get progress text for consent status
   */
  getConsentProgressText(): string {
    const { given, total } = this.getConsentCount();
    if (given === 0) {
      return this.translate.instant('WIZARD_NO_CONSENTS_YET');
    }
    return this.translate.instant('WIZARD_CONSENTS_PROGRESS', { given, total });
  }

  /**
   * Prevent automatic form submit - ONLY allow manual button click
   */
  preventFormSubmit(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    console.warn('[AcceptInvitation] Form auto-submit prevented! Use the button instead.');
  }

  /**
   * Proceed to Step 2 (Password Creation)
   */
  proceedToStep2(): void {
    if (!this.canProceedToStep2) {
      // User clicked disabled button - scroll to checkboxes
      this.scrollToCheckboxes();
      return;
    }

    this.isTransitioning = true;
    setTimeout(() => {
      this.currentStep = 2;
      this.isTransitioning = false;
      // Scroll to top for better UX
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 300);
  }

  /**
   * Scroll to consent checkboxes with visual highlight
   * Called when user clicks disabled button
   */
  scrollToCheckboxes(): void {
    const checkboxesElement = document.getElementById('consent-checkboxes-section');
    if (checkboxesElement) {
      // Smooth scroll to checkboxes with offset for better visibility
      const elementPosition = checkboxesElement.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - 20; // 20px offset from top

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });

      // Add highlight animation
      checkboxesElement.classList.add('highlight-pulse');
      setTimeout(() => {
        checkboxesElement.classList.remove('highlight-pulse');
      }, 2000);
    }
  }

  /**
   * Go back to Step 1 (GDPR Consent)
   */
  backToStep1(): void {
    this.isTransitioning = true;
    setTimeout(() => {
      this.currentStep = 1;
      this.isTransitioning = false;
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 300);
  }

  onSubmit(): void {
    // Extra validation: Only submit if explicitly called AND valid
    if (!this.newPasswordForm.valid) {
      console.warn('[AcceptInvitation] Form invalid - cannot submit');
      return;
    }

    // Prevent multiple submissions
    if (this.isTransitioning) {
      console.warn('[AcceptInvitation] Already submitting - prevented duplicate');
      return;
    }

    this.isTransitioning = true;
    this.submitError = null;
    this.showLoginAction = false;

    if (this.newPasswordForm.valid) {
      // ========================================
      // DSGVO Consent Request (Added: 2025-10-19)
      // ========================================
      const requestData: EmployeeAccountConfirmationRequest = {
        Email: this.email,
        NewPassword: this.newPasswordForm.get('password')?.value,
        OldPassword: this.oldPass,
        Token: this.token,
        InvitationId: this.iid,

        // DSGVO Consent (Backend erwartet PascalCase!)
        // Eingeladene Mitarbeiter akzeptieren KEINE B2B-AGB – Vertragspartei ist der
        // Arbeitgeber. TermsAccepted bleibt aus Payload-Kompatibilität false.
        // Datenschutz = Kenntnisnahme (Art. 13 DSGVO).
        TermsAccepted: false,
        PrivacyPolicyAccepted: this.newPasswordForm.get('privacyPolicyAccepted')?.value || false,
        ConsentVersion: GDPR_CONSTANTS.CURRENT_CONSENT_VERSION,
        MarketingConsent: this.newPasswordForm.get('marketingConsent')?.value || false,
        UserAgent: this.userAgent,
        // IP address wird server-seitig aus HttpContext ermittelt
      };

      this.authService.EmployeeAccountConfirmation(requestData, (success, errorCodes) => {
        // Nach einer Ablehnung (z. B. ungültiger Link, Passwortregel) muss ein neuer Versuch
        // möglich sein; bei Erfolg navigiert der AuthService bereits zum Login.
        if (!success) {
          this.isTransitioning = false;
          const codes = errorCodes ?? [];
          this.submitError = employeeConfirmationErrorText(this.translate, codes);
          this.showLoginAction = codes.includes('TOKEN_INVALID_OR_EXPIRED');
        }
      });
    }
  }

  /** Weg aus dem Sammeltext: Wer schon ein Konto hat, meldet sich an. */
  goToLogin(): void {
    this.authService.goToLogin();
  }

  toggleTheme(): void {
    this.themeService.toggleTheme();
  }

  getThemeIconClass(): string {
    const mode = this.themeService.getThemeIcon();
    if (mode === 'brightness_auto') return 'fa-circle-half-stroke';
    if (mode === 'light_mode') return 'fa-sun';
    return 'fa-moon';
  }
}
